import { useRef, useState, useCallback, useEffect } from 'react'

// The SDK should be included synchronously on the page (e.g. in `public/index.html`):
// <script src="/libs/speech-sdk.js"></script>
// We assume the bundle exposes `window.SpeechSDK`. If it's not present, start() will fail with a clear message.
function getSpeechSDK(): any {
  if (typeof window === 'undefined') return null
  return (window as any).SpeechSDK || null
}

type Source = 'microphone' | 'trtc'

export type StartOptions = {
  source?: Source
  // optional MediaStream when source==='trtc' (e.g., from TRTC local stream)
  sourceStream?: MediaStream | null
  subscriptionKey?: string
  region?: string
  languageFrom?: string
  languageTo?: string
  enableTrueText?: boolean
  segmentationTimeoutMs?: number
  phraseList?: string[]
}

export type Recognized = {
  text: string
  translation?: string
  timestamp: number
  isFinal?: boolean
}

export default function useSpeechTranslator() {
  // 用于防止stop后旧回调污染新流
  const sessionIdRef = useRef(0)
  // 文本翻译方法（用于短语命中时重新翻译）
  const translateText = useCallback(async ({ text, languageFrom, languageTo, subscriptionKey, region }: {
    text: string,
    languageFrom?: string,
    languageTo?: string,
    subscriptionKey?: string,
    region?: string
  }) => {
    // Azure Translator API实现
    const envKey = (process && (process.env as any)?.REACT_APP_TRANSLATOR_KEY) || ''
    const envRegion = (process && (process.env as any)?.REACT_APP_TRANSLATOR_REGION) || ''
    const keyToUse = subscriptionKey || envKey
    const regionToUse = region || envRegion || 'eastasia'
    if (!keyToUse) return { translation: '' }
    // 语言码映射
    const langMap: Record<string, string> = {
      'en-US': 'en', 'en-GB': 'en', 'es-ES': 'es', 'es-MX': 'es', 'fr-FR': 'fr', 'de-DE': 'de', 'it-IT': 'it', 'pt-BR': 'pt', 'zh-CN': 'zh-Hans', 'ja-JP': 'ja', 'ko-KR': 'ko', 'ru-RU': 'ru', 'ar-SA': 'ar', 'hi-IN': 'hi', 'nl-NL': 'nl', 'sv-SE': 'sv', 'no-NO': 'no', 'da-DK': 'da', 'fi-FI': 'fi', 'pl-PL': 'pl'
    }
    const fromLang = langMap[languageFrom || 'en-US'] || (languageFrom || 'en').split('-')[0]
    const toLang = langMap[languageTo || 'zh-CN'] || (languageTo || 'zh').split('-')[0]
    const endpoint = `https://api.cognitive.microsofttranslator.com/translate?api-version=3.0&from=${fromLang}&to=${toLang}`
    const body = [{ text }]
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Ocp-Apim-Subscription-Key': keyToUse,
          'Ocp-Apim-Subscription-Region': regionToUse,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })
      const data = await res.json()
      if (Array.isArray(data) && data[0]?.translations?.[0]?.text) {
        return { translation: data[0].translations[0].text }
      }
      return { translation: '' }
    } catch (e) {
      return { translation: `[Translation Error] ${text}` }
    }
  }, [])
  const recognizerRef = useRef<any>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const destinationRef = useRef<MediaStream | null>(null)
  const [status, setStatus] = useState<'idle'|'starting'|'running'|'stopped'|'error'>('idle')
  const [lastResult, setLastResult] = useState<Recognized | null>(null)

  const stop = useCallback(() => {
    sessionIdRef.current += 1; // 使所有旧回调失效
    try {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          try { recognizerRef.current.close(); } catch (e) {}
          recognizerRef.current = null
          setStatus('stopped')
        }, (err: any) => {
          console.warn('stop error', err)
          recognizerRef.current = null
          setStatus('stopped')
        })
      }
    } catch (e) {
      console.warn('stop recognizer', e)
      recognizerRef.current = null
      setStatus('stopped')
    }

    // stop any created media tracks (only those we created)
    if (destinationRef.current) {
      destinationRef.current.getTracks().forEach(t => t.stop())
      destinationRef.current = null
    }
    if (mediaStreamRef.current) {
      // if we created it (microphone) don't leak; if passed in (trtc) we should NOT stop it
      // we rely on caller to manage passed-in stream
      mediaStreamRef.current = null
    }
  }, [])

  // SDK is statically imported and `window.SpeechSDK` already set above.

  const start = useCallback(async (opts: StartOptions = {}, onRecognized?: (r: Recognized) => void) => {
    sessionIdRef.current += 1;
    const mySessionId = sessionIdRef.current;
    const SpeechSDK = getSpeechSDK()
    if (!SpeechSDK) {
      console.warn('Speech SDK not found. Please include the browser bundle in your page, e.g.')
      setStatus('error')
      return false
    }
    setStatus('starting')

    try {
      const {
        source = 'microphone',
        sourceStream = null,
        subscriptionKey,
        region = 'eastasia',
        languageFrom = 'en-US',
        languageTo = 'zh-CN',
        enableTrueText = true,
        segmentationTimeoutMs = 500,
        phraseList = [],
      } = opts
      console.log('SpeechSDK === opts', opts)
      // Allow configuring subscription from environment variables (CRA uses REACT_APP_ prefix)
      const envKey = (process && (process.env as any)?.REACT_APP_SPEECH_KEY) || ''
      const envRegion = (process && (process.env as any)?.REACT_APP_SPEECH_REGION) || ''
      const keyToUse = subscriptionKey || envKey
      const regionToUse = region || envRegion || 'eastasia'

      if (!keyToUse) {
        console.warn('Speech subscription key not provided. Set `REACT_APP_SPEECH_KEY` in your .env or pass it to start()')
        setStatus('error')
        return false
      }

      const speechConfig = SpeechSDK.SpeechTranslationConfig.fromSubscription(keyToUse, regionToUse)
      speechConfig.speechRecognitionLanguage = languageFrom
      speechConfig.addTargetLanguage(languageTo)

      if (enableTrueText) {
        try {
          speechConfig.setServiceProperty('speech.truetext', 'true', SpeechSDK.ServicePropertyChannel.UriQueryParameter)
        } catch (e) {}
      }

      try {
        speechConfig.setServiceProperty('speech.segmentation.silenceTimeoutMs', `${segmentationTimeoutMs}`, SpeechSDK.ServicePropertyChannel.UriQueryParameter)
      } catch (e) {}

      let audioConfig: any = null

      if (source === 'microphone') {
        audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
        mediaStreamRef.current = null
      } else if (source === 'trtc' && sourceStream) {
        // try to clone stream; if not available, route via AudioContext
        try {
          const cloned = (typeof sourceStream.clone === 'function') ? sourceStream.clone() : null
          if (cloned) {
            mediaStreamRef.current = cloned
            audioConfig = SpeechSDK.AudioConfig.fromStreamInput(cloned)
          } else {
            // route via AudioContext -> destination
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
            const src = ctx.createMediaStreamSource(sourceStream)
            const dest = ctx.createMediaStreamDestination()
            src.connect(dest)
            destinationRef.current = dest.stream
            audioConfig = SpeechSDK.AudioConfig.fromStreamInput(dest.stream)
          }
        } catch (e) {
          console.warn('failed to prepare trtc stream for recognizer', e)
          audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
        }
      } else {
        audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
      }

      const recognizer = new SpeechSDK.TranslationRecognizer(speechConfig, audioConfig)
      recognizerRef.current = recognizer

      // apply phrase list if provided
      if (phraseList && phraseList.length > 0) {
        try {
          const phraseListGrammar = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer)
          phraseList.forEach(p => phraseListGrammar.addPhrase(p))
        } catch (e) {
          console.warn('apply phrase list failed', e)
        }
      }

      recognizer.recognizing = (_s: any, e: any) => {
        if (sessionIdRef.current !== mySessionId) return; // 丢弃旧回调
        try {
          if (!e || !e.result) return
          const now = Date.now()
          let text = ''
          let translation = ''
          try { text = e.result.text || '' } catch (err) {}
          try { translation = e.result.translations.get(languageTo) || '' } catch (err) {}
          const payload = { text, translation, timestamp: now, isFinal: false, languageTo };
          console.log('[SpeechTranslator] recognizing', { sessionId: mySessionId, languageTo, text, translation, payload });
          setLastResult(payload)
          if (onRecognized) onRecognized(payload)
        } catch (err) {
          // swallow
        }
      }

      recognizer.recognized = (_s: any, e: any) => {
        if (sessionIdRef.current !== mySessionId) return; // 丢弃旧回调
        if (!e || !e.result) return
        const now = Date.now()
        let text = ''
        let translation = ''
        try { text = e.result.text || '' } catch (err) {}
        try {
          if (e.result.reason === SpeechSDK.ResultReason.TranslatedSpeech) {
            const translations = e.result.translations
            translation = translations.get(languageTo) || ''
          }
        } catch (err) {}

        const payload = { text, translation, timestamp: now, isFinal: true, languageTo };
        console.log('[SpeechTranslator] recognized', { sessionId: mySessionId, languageTo, text, translation, payload });
        setLastResult(payload)
        if (onRecognized) onRecognized(payload)
      }

      recognizer.canceled = (_s: any, e: any) => {
        if (sessionIdRef.current !== mySessionId) return; // 丢弃旧回调
        console.warn('recognizer canceled', e)
        setStatus('error')
      }

      recognizer.sessionStarted = () => { if (sessionIdRef.current === mySessionId) setStatus('running') }
      recognizer.sessionStopped = () => { if (sessionIdRef.current === mySessionId) setStatus('stopped') }

      recognizer.startContinuousRecognitionAsync(() => {
        if (sessionIdRef.current === mySessionId) setStatus('running')
      }, (err: any) => {
        if (sessionIdRef.current === mySessionId) {
          console.error('startContinuousRecognitionAsync err', err)
          setStatus('error')
        }
      })

      return true
    } catch (err) {
      console.error('start translator error', err)
      setStatus('error')
      return false
    }
  }, [])

  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return {
    start,
    stop,
    status,
    lastResult,
    translateText,
  }
}
